import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface TRMRecord {
  id: string;
  fecha: string;
  valor: number;
  moneda: string;
  vigencia_desde: string | null;
  vigencia_hasta: string | null;
  created_at: string;
}

/** Map currency → COP rate. Always includes COP: 1. */
export type TRMMapType = Record<string, number>;

/**
 * Fetches the latest TRM for ALL available currencies.
 * Returns a trmMap: Record<string, number> e.g. { USD: 4200, EUR: 4600, COP: 1 }
 */
export function useTRM() {
  const { data, isLoading, error } = useQuery<TRMMapType>({
    queryKey: ["trm-map"],
    queryFn: async () => {
      // Fetch latest record per currency using distinct on moneda ordered by fecha desc
      const { data, error } = await supabase
        .from("trm_historico")
        .select("*")
        .order("moneda", { ascending: true })
        .order("fecha", { ascending: false });

      if (error) throw error;

      const map: TRMMapType = { COP: 1 };
      const seen = new Set<string>();

      // Records come ordered by moneda asc, fecha desc — first per moneda is latest
      for (const row of (data ?? [])) {
        const m = (row.moneda as string) ?? "USD";
        if (!seen.has(m)) {
          seen.add(m);
          map[m] = row.valor as number;
        }
      }

      // Ensure USD always has a key even if DB is empty
      if (!map["USD"]) map["USD"] = 0;

      return map;
    },
    staleTime: 1000 * 60 * 60, // 1 hour cache
  });

  const trmMap = data ?? { COP: 1 };
  const availableCurrencies = Object.keys(trmMap)
    .filter((c) => c === "COP" || (trmMap[c] ?? 0) > 0)
    .sort();

  return {
    /** Full map: currency → COP rate */
    trmMap,
    /** Currency codes present in trm_historico (with rate>0) + COP. */
    availableCurrencies,
    /** Shortcut: USD rate (backward compat) */
    trm: trmMap["USD"] ?? null,
    /** Date of last USD record (kept for KPI display) */
    trmFecha: null as string | null, // we no longer track single fecha
    trmLoading: isLoading,
    trmError: error,
  };
}

/**
 * Standalone hook that returns the latest fecha for a specific currency (for display).
 */
export function useTRMFecha(moneda: string = "USD") {
  const { data } = useQuery<string | null>({
    queryKey: ["trm-fecha", moneda],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trm_historico")
        .select("fecha")
        .eq("moneda", moneda)
        .order("fecha", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data?.fecha as string) ?? null;
    },
    staleTime: 1000 * 60 * 60,
  });
  return data ?? null;
}
